import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  Chip,
  Stack,
  Avatar,
  Paper,
  Divider,
  IconButton,
  Rating,
  Skeleton,
  Dialog,
  DialogContent,
  useTheme
} from '@mui/material';
import {
  LocalShipping,
  Security,
  Support,
  Star,
  TrendingUp,
  FlashOn,
  ShoppingCart,
  Favorite,
  Share,
  ArrowForward,
  CheckCircle,
  Speed,
  Eco,
  HeadsetMic,
  WhatsApp,
  Phone,
  Close,
  ChevronLeft,
  ChevronRight,
  NavigateNext,
  NavigateBefore
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import { homeService } from '../services/homeService';
import ImageCard from '../components/ImageCard';
import FAQ from '../components/FAQ';
import Testimonials from '../components/Testimonials';
import CTA from '../components/CTA';
import { keyframes } from "@emotion/react";

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [validImages, setValidImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);
  const [productImageIndices, setProductImageIndices] = useState({});

  // Fonction pour vérifier si on peut scroller
  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      // Ajouter une marge de 5px pour éviter les problèmes d'arrondi
      const tolerance = 5;
      setCanScrollLeft(scrollLeft > tolerance);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - tolerance);
    }
  }, []);

  // Vérifier au montage et après le chargement des plans
  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [subscriptionPlans, checkScrollButtons]);

  // Fonctions de navigation
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      // Obtenir la largeur du premier enfant (première carte)
      const firstChild = scrollContainerRef.current.children[0];
      const cardWidth = firstChild ? firstChild.offsetWidth : 400;
      const gap = 24; // gap: 3 = 24px en Material-UI
      scrollContainerRef.current.scrollBy({
        left: -(cardWidth + gap),
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      // Obtenir la largeur du premier enfant (première carte)
      const firstChild = scrollContainerRef.current.children[0];
      const cardWidth = firstChild ? firstChild.offsetWidth : 400;
      const gap = 24; // gap: 3 = 24px en Material-UI
      scrollContainerRef.current.scrollBy({
        left: cardWidth + gap,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    fetchLatestProducts();
    fetchSubscriptionPlans();
  }, []);

  // Gérer le scroll vers la section FAQ si l'URL contient #faq
  useEffect(() => {
    const handleHashScroll = () => {
      if (window.location.hash === '#faq') {
        // Attendre que le DOM soit complètement chargé avec plusieurs tentatives
        let attempts = 0;
        const maxAttempts = 10;

        const scrollToFAQ = () => {
          attempts++;
          const faqSection = document.getElementById('faq');
          if (faqSection) {
            // Délai pour s'assurer que tout est rendu et que les images sont chargées
            setTimeout(() => {
              const rect = faqSection.getBoundingClientRect();
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              const targetPosition = rect.top + scrollTop - 80; // 80px pour la navbar

              window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
              });
            }, 300);
          } else if (attempts < maxAttempts) {
            // Si la section n'est pas encore disponible, réessayer
            setTimeout(scrollToFAQ, 200);
          }
        };

        // Démarrer après un court délai pour laisser React rendre
        setTimeout(scrollToFAQ, 100);
      }
    };

    // Vérifier après un délai pour laisser le composant se monter
    const timer = setTimeout(handleHashScroll, 500);

    // Écouter les changements de hash
    window.addEventListener('hashchange', handleHashScroll);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('hashchange', handleHashScroll);
    };
  }, []);


  const shine = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

  const fetchSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await homeService.getSubscriptionPlans();
      // homeService.getSubscriptionPlans() retourne response.data de axios
      // qui est { success: true, data: [...], count: ... }
      // Donc response = { success: true, data: [...], count: ... }
      // console.log('Réponse complète des formules:', response);

      // Si response.data existe et est un tableau, l'utiliser
      // Sinon, vérifier si response est directement un tableau
      let plans = [];
      if (response?.data && Array.isArray(response.data)) {
        plans = response.data;
      } else if (Array.isArray(response)) {
        plans = response;
      } else if (response?.success && response?.data) {
        plans = response.data;
      }

      // console.log('Formules extraites:', plans);
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Erreur lors du chargement des formules:', error);
      console.error('Détails de l\'erreur:', error.response?.data || error.message);
      setSubscriptionPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchLatestProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts({ sortBy: 'newest' });
      const products = response.products || [];
      // Limiter à 9 produits
      setLatestProducts(products.slice(0, 15));
    } catch (error) {
      console.error('Error fetching latest products:', error);
    } finally {
      setLoading(false);
    }
  };





  // Fonction helper pour filtrer les images valides (inclut maintenant les images par défaut)
  const filterValidImages = useCallback((images) => {
    return images.filter(img => {
      // Inclure toutes les images non-null et non-undefined, même les images par défaut
      return img && typeof img === 'string' && img.trim() !== '';
    });
  }, []);

  // Fonctions pour naviguer entre les images dans les cartes
  const handleProductImageNext = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const handleProductImagePrev = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleImageClick = (product) => {
    // Vérifier qu'il y a des images avant d'ouvrir le modal
    const images = product?.images || [];

    // Filtrer les images valides (inclut maintenant les images par défaut)
    const validImagesArray = filterValidImages(images);

    // Ne pas ouvrir si aucune image
    if (validImagesArray.length === 0) {
      return;
    }

    // Précharger toutes les images pour éviter le clignotement
    validImagesArray.forEach(imgSrc => {
      if (imgSrc) {
        const img = new Image();
        img.src = imgSrc;
      }
    });

    setSelectedProduct(product);
    setValidImages(validImagesArray);
    setCurrentImageIndex(0);
    setImageModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setImageModalOpen(false);
    setSelectedProduct(null);
    setValidImages([]);
    setCurrentImageIndex(0);
  }, []);

  const handleNextImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
  }, [validImages.length]);

  const handlePreviousImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + validImages.length) % validImages.length);
  }, [validImages.length]);

  const handleThumbnailClick = useCallback((index) => {
    if (index >= 0 && index < validImages.length && index !== currentImageIndex) {
      setCurrentImageIndex(index);
    }
  }, [validImages.length, currentImageIndex]);

  const ProductSkeleton = () => (
    <Card>
      <Skeleton variant="rectangular" height={200} />
      <CardContent>
        <Skeleton variant="text" height={32} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  // Corriger l'index si invalide quand les images valides changent
  useEffect(() => {
    if (validImages.length > 0 && currentImageIndex >= validImages.length) {
      setCurrentImageIndex(0);
    }
  }, [validImages.length, currentImageIndex]);

  // Mémoriser les images pour éviter les recalculs
  const modalImages = useMemo(() => {
    return validImages.length > 0 ? validImages : (selectedProduct?.images || []);
  }, [validImages, selectedProduct]);

  // Mémoriser l'index sûr
  const safeIndex = useMemo(() => {
    if (modalImages.length === 0) return 0;
    return currentImageIndex >= modalImages.length ? 0 : currentImageIndex;
  }, [modalImages.length, currentImageIndex]);

  const ImageModal = () => {
    const currentImage = modalImages[safeIndex];

    return (
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        keepMounted={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            width: '85vw',
            maxWidth: '900px',
            maxHeight: '85vh',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            position: 'relative',
            margin: 0,
            transition: 'none !important'
          }
        }}
        BackdropProps={{
          sx: {
            transition: 'none !important',
            animation: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            opacity: '1 !important'
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-root': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiBackdrop-root': {
            transition: 'none !important',
            animation: 'none !important',
            opacity: '1 !important'
          },
          '& .MuiDialog-paper': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-paperScrollBody': {
            transition: 'none !important',
            animation: 'none !important'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            maxHeight: '75vh',
            overflow: 'hidden',
            transition: 'none !important',
            '&.MuiDialogContent-root': {
              transition: 'none !important'
            }
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: '#f97316',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(171, 56, 56, 0.3)'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Previous button */}
          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: '#f97316',
                color: 'white',
                '&:hover': {
                  bgcolor: 'white'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}

          {/* Next button */}
          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: '#f97316',
                color: 'white',
                '&:hover': {
                  bgcolor: '#f97316'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          {/* Image container */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              userSelect: 'none'
            }}
          >
            {currentImage ? (
              <Box
                component="img"
                key={`img-${safeIndex}`}
                src={currentImage}
                alt={selectedProduct?.name || 'Product image'}
                loading="eager"
                onError={(e) => {
                  // Si l'image échoue et ce n'est pas déjà l'image par défaut, utiliser l'image par défaut
                  if (!e.target.src.includes('no_media')) {
                    e.target.src = '/img/no_media.jpg';
                  }
                }}
                onLoad={(e) => {
                  // Précharger les images adjacentes pour éviter le clignotement
                  if (modalImages.length > 1) {
                    const nextIndex = (safeIndex + 1) % modalImages.length;
                    const prevIndex = (safeIndex - 1 + modalImages.length) % modalImages.length;

                    [nextIndex, prevIndex].forEach(idx => {
                      if (modalImages[idx] && modalImages[idx] !== currentImage) {
                        const preloadImg = new Image();
                        preloadImg.src = modalImages[idx];
                      }
                    });
                  }
                }}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  transition: 'opacity 0.15s ease-in-out',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                  imageRendering: 'auto',
                  display: 'block',
                  visibility: 'visible'
                }}
              />
            ) : (
              <Box
                component="img"
                src="/img/no_media.jpg"
                alt="No image"
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>

          {/* Image counter - style ProductDetail */}
          {modalImages.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                zIndex: 10
              }}
            >
              {safeIndex + 1} / {modalImages.length}
            </Box>
          )}
        </DialogContent>

        {/* Miniatures - style ProductDetail */}
        {modalImages.length > 1 && (
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 0.75,
              overflowX: 'auto',
              borderTop: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.5)'
                }
              }
            }}
          >
            {modalImages.map((image, index) => {
              const isActive = safeIndex === index;
              return (
                <Box
                  key={`thumb-${index}-${image?.substring(0, 30) || index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleThumbnailClick(index);
                  }}
                  sx={{
                    minWidth: 70,
                    width: 70,
                    height: 50,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: isActive ? 'default' : 'pointer',
                    border: isActive
                      ? '2px solid #1976d2'
                      : '2px solid transparent',
                    opacity: isActive ? 1 : 0.7,
                    flexShrink: 0,
                    '&:hover': {
                      opacity: isActive ? 1 : 1,
                      borderColor: isActive ? '#1976d2' : 'rgba(255, 255, 255, 0.5)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={`${selectedProduct?.name || 'Image'} ${index + 1}`}
                    onError={(e) => {
                      if (!e.target.src.includes('no_media')) {
                        e.target.src = '/img/no_media.jpg';
                      }
                    }}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none'
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Dialog>
    );
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          setMousePosition({ x, y });
        }}
        onMouseLeave={() => {
          setMousePosition({ x: 50, y: 50 });
        }}
        sx={{
          background: 'linear-gradient(90deg, #f97316,rgba(215, 95, 95, 0.77),rgba(243, 209, 226, 0.25), 166, 195, 0.25))',
          backgroundSize: '300% 300%',
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          animation: 'gradientMove 6s ease infinite',
          borderBottom: '0px solid #e0e0e0',
          '@keyframes gradientMove': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}

      >
        {/* Mouse Effect - Light Gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle 500px at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 30%, transparent 60%)`,
            transition: 'background 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        {/* Floating Particles Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 0,
            background: `
              radial-gradient(circle 2px at ${mousePosition.x * 0.8}% ${mousePosition.y * 0.8}%, rgba(255, 255, 255, 0.3) 0%, transparent 50%),
              radial-gradient(circle 1.5px at ${mousePosition.x * 1.2}% ${mousePosition.y * 1.1}%, rgba(255, 255, 255, 0.25) 0%, transparent 50%),
              radial-gradient(circle 1px at ${mousePosition.x * 0.9}% ${mousePosition.y * 1.15}%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)
            `,
            transition: 'background 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: 0.6
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={4} alignItems="center" sx={{ textAlign: 'center' }}>
                <Chip
                  icon={<FlashOn />}
                  label="Bienvenu(e) sur notre marketplace"
                  color="secondary"
                  sx={{
                    fontSize: '0.9rem',
                    py: 2,
                    px: 1
                  }}
                />
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                    color: '#9b59b6',
                    animation: 'bounce 6s ease-in-out infinite',
                    '@keyframes bounce': {
                      '0%, 100%': {
                        transform: 'translateY(0) scale(1)'
                      },
                      '25%': {
                        transform: 'translateY(-4px) scale(1.02)'
                      },
                      '50%': {
                        transform: 'translateY(0) scale(1)'
                      },
                      '75%': {
                        transform: 'translateY(-4px) scale(1.01)'
                      }
                    }
                  }}
                >
                  Découvrez l'excellence
                  <Box component="span" sx={{
                    color: '#9b59b6',
                    display: 'block'
                  }}>
                    locale de Tchamba
                  </Box>
                </Typography>
                {/* <Typography
                  variant="h5" 
                  sx={{ 
                    opacity: 0.9,
                    fontWeight: 400,
                    color: '#9b59b6',
                    fontSize: { xs: '1.1rem', md: '1.3rem' }
                  }}
                >
                  Plus de 10,000 produits de qualité à des prix imbattables
                </Typography> */}
                <Box
                  sx={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                    px: 3,
                    py: 1,
                    textAlign: 'center',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    width: '100%',
                    maxWidth: '600px'
                  }}
                >
                  <Typography variant="h4" gutterBottom sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    background: "linear-gradient(90deg, #a855f7, #ec4899, #a855f7)",
                    backgroundSize: "200% auto",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: `${shine} 3s linear infinite`,
                    display: "inline-block",
                  }}>
                    Produits authentiques - Services de qualité - Commerce local
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/products')}
                    endIcon={<ArrowForward />}
                    sx={{
                      background:theme.palette.color.degradeOne,
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Découvrez les produits
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/boutiques')}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      backgroundColor: '#6832BC',
                      borderRadius: 3,
                      // '&:hover': {
                      //   borderColor: '#6832BC',
                      //   bgcolor: 'white',
                      //   color: '#6832BC',
                      //   transform: 'translateY(-2px)',
                      //   boxShadow: 4
                      // },
                      // transition: 'all 0.3s ease'
                    }}
                  >
                    Voir les boutiques
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
        {/* Latest Products Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Nouveautés
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Découvrez les derniers produits publiés
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={3} sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gridAutoRows: '1fr'
          }}>
            {[...Array(9)].map((_, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <ProductSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : latestProducts.length === 0 ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Aucun produit disponible
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gridAutoRows: '1fr'
            }}>
              {latestProducts.map((product) => (
                <Grid item xs={12} sm={6} lg={3} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImageClick(product)}
                    >
                      {(() => {
                        const productImages = product.images || [];
                        const currentIndex = productImageIndices[product.id] || 0;
                        const hasMultipleImages = productImages.length > 1;
                        const currentImage = productImages[currentIndex] || productImages[0];

                        return (
                          <>
                            <ImageCard
                              src={currentImage}
                              alt={product.name}
                              height={200}
                            />

                            {/* Navigation entre images */}
                            {hasMultipleImages && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleProductImagePrev(e, product.id, productImages.length)}
                                  sx={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    '&:hover': { bgcolor: 'white' },
                                    zIndex: 2
                                  }}
                                >
                                  <ChevronLeft fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleProductImageNext(e, product.id, productImages.length)}
                                  sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    '&:hover': { bgcolor: 'white' },
                                    zIndex: 2
                                  }}
                                >
                                  <ChevronRight fontSize="small" />
                                </IconButton>
                                {/* Indicateur du nombre d'images */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    zIndex: 2
                                  }}
                                >
                                  {currentIndex + 1} / {productImages.length}
                                </Box>
                              </>
                            )}
                          </>
                        );
                      })()}

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          gap: 1,
                          zIndex: 2
                        }}
                      >
                        {product.isVip && (
                          <Chip
                            label="VIP"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {/* <IconButton
                          size="small"
                          sx={{
                            bgcolor: 'white',
                            '&:hover': { bgcolor: 'grey.100' }
                          }}
                        >
                          <Favorite fontSize="small" />
                        </IconButton> */}
                      </Box>
                      {(!product.isAvailable || product.stockQuantity === 0) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            bgcolor: 'error.main',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            zIndex: 3
                          }}
                        >
                          En rupture
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {product.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                            {parseFloat(product.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
                          </Typography>
                          <Typography
                            style={{
                              textDecoration: product.isAvailable ? "none" : "none",
                              color: product.isAvailable ? "green" : "red",
                            }}
                          >
                            {product.isAvailable ? (
                              <>
                                Disponible : {product.stockQuantity || 0}
                              </>
                            ) : (
                              <>
                                Stock : 0
                              </>
                            )}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                          <Button
                            // variant="outlined"
                            fullWidth
                            sx={{ background:theme.palette.color.degradeOne, color: 'white' }}
                            onClick={() => navigate(`/products/${product.id}`)}
                            // disabled={product.stockQuantity === 0}
                          >
                            Détails
                          </Button>
                          {(() => {
                            // Si l'utilisateur a une boutique, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
                            const contactPhone = product.boutique
                              ? (product.boutique.whatsapp || product.boutique.phone)
                              : product.user?.phone;
                            return contactPhone && (
                              <>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  startIcon={<WhatsApp />}
                                  onClick={() => {
                                    const phoneNumber = contactPhone;
                                    const message = `Bonjour, je suis intéressé par le produit "${product.name}"${product.boutique ? ` de ${product.boutique.name}` : ''}`;
                                    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
                                  }}
                                  sx={{
                                    bgcolor: '#25D366',
                                    '&:hover': { bgcolor: '#128C7E' },
                                    '& .MuiButton-startIcon': {
                                      marginRight: '1px'
                                    }
                                  }}
                                >
                                  WhatsApp
                                </Button>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<Phone />}
                                  onClick={() => {
                                    const phoneNumber = contactPhone;
                                    window.open(`tel:${phoneNumber}`, '_self');
                                  }}
                                  sx={{
                                    borderColor: 'blue.500',
                                    color: 'blue.500',
                                    '&:hover': { borderColor: 'blue.500', bgcolor: 'white', color: 'blue.500' },
                                    '& .MuiButton-startIcon': {
                                      marginRight: '1px'
                                    }
                                  }}
                                >
                                  Appeler
                                </Button>
                              </>
                            );
                          })()}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Button to redirect to Products page */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/products')}
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Voir tous les produits
              </Button>
            </Box>
          </>
        )}

        {/* Pricing Section */}
        <Box sx={{ mt: 8, mb: 6, position: 'relative' }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Nos formules d'abonnement
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Choisissez la formule qui correspond à vos besoins
            </Typography>
          </Box>

          {/* Flèche gauche */}
          {subscriptionPlans.length > 0 && canScrollLeft && (
            <IconButton
              onClick={scrollLeft}
              sx={{
                position: 'absolute',
                left: { xs: 0, sm: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                width: 48,
                height: 48,
                '&:hover': {
                  background: 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease',
                display: { xs: 'none', sm: 'flex' },
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <NavigateBefore fontSize="large" />
            </IconButton>
          )}

          {/* Flèche droite */}
          {subscriptionPlans.length > 0 && canScrollRight && (
            <IconButton
              onClick={scrollRight}
              sx={{
                position: 'absolute',
                right: { xs: 0, sm: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                width: 48,
                height: 48,
                '&:hover': {
                  background: 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease',
                display: { xs: 'none', sm: 'flex' },
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <NavigateNext fontSize="large" />
            </IconButton>
          )}

          {loadingPlans ? (
            <Grid container spacing={3} justifyContent="center">
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent>
                      <Skeleton variant="text" height={32} />
                      <Skeleton variant="text" height={20} />
                      <Skeleton variant="text" height={20} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : subscriptionPlans.length > 0 ? (
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                overflowY: 'hidden',
                px: 2,
                pb: 2,
                // Scrollbar invisible
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                // Smooth scroll
                scrollBehavior: 'smooth',
                // Empêcher le scroll vertical
                '& > *': {
                  flexShrink: 0
                }
              }}
              onScroll={checkScrollButtons}
            >
              {subscriptionPlans.map((plan) => (
                <Box
                  key={plan.id}
                  sx={{
                    minWidth: { xs: '85%', sm: '400px', md: '350px' },
                    maxWidth: { xs: '85%', sm: '400px', md: '350px' }
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      border: plan.populaire ? '2px solid #f97316' : '1px solid',
                      borderColor: plan.populaire ? '#f97316' : 'divider',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8
                      },
                      position: 'relative'
                    }}
                  >
                    {plan.populaire && (
                      <Chip
                        label="Populaire"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          fontWeight: 600,
                          bgcolor: '#f97316',
                          color: 'white'
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                          borderRadius: 3,
                          px: 2.5,
                          py: 1,
                          mb: 2,
                          boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)',
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: 'white',
                            position: 'relative',
                            zIndex: 1,
                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          {plan.libelle || 'Formule'}
                        </Typography>
                      </Box>
                      {plan.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 60 }}>
                          {plan.description}
                        </Typography>
                      )}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {plan.prix !== undefined && plan.prix !== null
                            ? `${parseFloat(plan.prix).toLocaleString('fr-FR')} FCFA`
                            : 'Sur demande'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          par mois
                        </Typography>
                      </Box>

                      {/* Options/Features */}
                      <Stack spacing={1.5} sx={{ mb: 3 }}>
                        {plan.nombreProduit !== undefined && plan.nombreProduit !== null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2">
                              {plan.nombreProduit === 0 ? 'Produits illimités' : `${plan.nombreProduit} produit${plan.nombreProduit > 1 ? 's' : ''}`}
                            </Typography>
                          </Box>
                        )}
                        {plan.code && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2">
                              Code: {plan.code}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                      {/* 
                      <Button
                        variant={plan.populaire ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        onClick={() => navigate('/vip')}
                        sx={{
                          mt: 'auto',
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 2,
                          ...(plan.populaire && {
                            background: 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #ef4444, #ec4899, #f97316)',
                              transform: 'translateY(-2px)'
                            }
                          })
                        }}
                      >
                        Choisir cette formule
                      </Button> */}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Aucune formule d'abonnement disponible pour le moment.
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      {/* Testimonials Section */}
      <Testimonials />

      {/* FAQ Section */}
      <Box id="faq" sx={{ scrollMarginTop: '80px' }}>
        <FAQ />
      </Box>

      {/* CTA Section */}
      <CTA />

      {/* Image Modal */}
      <ImageModal />

    </Box>
  );
};

export default Home;
